import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useNotifications } from "@/context/NotificationsContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCircle, WarningCircle, Info, BellRinging, PaperPlaneTilt, DownloadSimple } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/constants";

const LEVEL_ICON = {
  urgent: { icon: WarningCircle, cls: "text-red-400" },
  warning: { icon: WarningCircle, cls: "text-amber-400" },
  info: { icon: Info, cls: "text-blue-400" },
};

export default function NotificationBell() {
  const { items, unread, markRead, markAll, enablePush, disablePush, pushOn, lastCheck } = useNotifications();
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  const lastCheckLabel = () => {
    if (!lastCheck) return "conectare…";
    const s = Math.max(0, Math.round((now - lastCheck) / 1000));
    if (s < 60) return "acum câteva secunde";
    const m = Math.round(s / 60);
    return m < 60 ? `acum ${m} min` : `acum ${Math.round(m / 60)} h`;
  };
  const [digestHour, setDigestHour] = useState(7);
  const [digestTomorrow, setDigestTomorrow] = useState(false);

  useEffect(() => {
    api.get("/me/notif-prefs").then((r) => {
      setDigestHour(r.data.digest_hour ?? 7);
      setDigestTomorrow(!!r.data.digest_tomorrow);
    }).catch(() => {});
  }, []);

  const savePrefs = async (patch) => {
    try { await api.put("/me/notif-prefs", patch); toast.success("Preferințe salvate"); }
    catch { toast.error("Nu am putut salva preferințele"); }
  };
  const sendTest = async () => {
    try { await api.post("/push/test"); toast.success("Notificare de test trimisă!"); }
    catch { toast.error("Nu am putut trimite notificarea de test"); }
  };

  const open = (n) => {
    if (!n.read) markRead(n.id);
    if (n.type === "bling_update") return;
    if (n.project_id) navigate(`/projects/${n.project_id}`);
  };

  const installBling = (n) => {
    if (!n.read) markRead(n.id);
    const token = localStorage.getItem("hgs_token");
    localStorage.setItem("bling_ack_version", localStorage.getItem("bling_ack_version") || "");
    window.location.href = `${API}/companion/download?auth=${token}`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative rounded-md p-2 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
        >
          <Bell size={20} weight="bold" />
          {unread > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="font-head text-sm font-bold">Notificări Bling</span>
          <div className="flex items-center gap-3">
            {pushOn ? (
              <span className="flex items-center gap-2 text-xs">
                <span className="flex items-center gap-1 text-emerald-400"><BellRinging size={14} weight="fill" /> Activat</span>
                <button onClick={disablePush} className="text-muted-foreground hover:text-red-400 hover:underline" data-testid="notif-disable-push">Dezactivează</button>
              </span>
            ) : (
              <button onClick={enablePush} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="notif-enable-push" title="Primește notificări native pe acest dispozitiv (Windows/Android/iOS)">
                <BellRinging size={14} weight="fill" /> Activează
              </button>
            )}
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-muted-foreground hover:text-foreground hover:underline" data-testid="notif-mark-all">
                Marchează citite
              </button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">Nicio notificare.</p>
          )}
          {items.map((n) => {
            const L = LEVEL_ICON[n.level] || LEVEL_ICON.info;
            if (n.type === "bling_update") {
              return (
                <div key={n.id} className={cn("border-b border-border px-4 py-3", !n.read && "bg-primary/[0.06]")} data-testid={`notif-item-${n.id}`}>
                  <div className="flex items-start gap-3">
                    <DownloadSimple size={18} weight="bold" className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold leading-tight">{n.title}</div>
                      {n.body && <div className="text-xs text-muted-foreground">{n.body}</div>}
                      <div className="mt-1 text-[10px] text-muted-foreground/70">{fmtDate(n.created_at)}</div>
                      <button onClick={() => installBling(n)} className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary/90" data-testid={`notif-install-${n.id}`}>
                        <DownloadSimple size={14} weight="bold" /> Instalează Bling
                      </button>
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </div>
                </div>
              );
            }
            return (
              <button
                key={n.id}
                onClick={() => open(n)}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-white/5",
                  !n.read && "bg-primary/[0.06]"
                )}
                data-testid={`notif-item-${n.id}`}
              >
                <L.icon size={18} weight="fill" className={cn("mt-0.5 shrink-0", L.cls)} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold leading-tight">{n.title}</div>
                  {n.body && <div className={cn("text-xs text-muted-foreground", n.type === "workflow" ? "whitespace-pre-line" : "truncate")}>{n.body}</div>}
                  <div className="mt-1 text-[10px] text-muted-foreground/70">{fmtDate(n.created_at)}</div>
                </div>
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>
        <div className="space-y-2 border-t border-border px-4 py-3 text-xs">
          <button onClick={sendTest} className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border py-1.5 font-semibold text-primary transition-colors hover:bg-primary/10" data-testid="notif-send-test">
            <PaperPlaneTilt size={14} weight="fill" /> Trimite-mi o notificare de test
          </button>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Rezumat de dimineață la ora</span>
            <select
              value={digestHour}
              onChange={(e) => { const h = Number(e.target.value); setDigestHour(h); savePrefs({ digest_hour: h }); }}
              className="rounded-md border border-border bg-card px-2 py-1 text-foreground"
              data-testid="notif-digest-hour"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
          <label className="flex cursor-pointer items-center justify-between">
            <span className="text-muted-foreground">Include și activitățile de mâine</span>
            <input
              type="checkbox"
              checked={digestTomorrow}
              onChange={(e) => { setDigestTomorrow(e.target.checked); savePrefs({ digest_tomorrow: e.target.checked }); }}
              className="h-4 w-4 accent-primary"
              data-testid="notif-digest-tomorrow"
            />
          </label>
          <div className="flex items-center gap-1.5 pt-1 text-[11px] text-muted-foreground" data-testid="notif-last-check">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Ultima verificare: {lastCheckLabel()}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
